import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addPlace, loadPlaces, setPlace, deletePlace, setPlacesLoadRequired,
  setPlacesIsLoading, initializePlaces } from '../redux/actions';
import { getPlacesList, getPlacesById, getPlace, getPlacesLoadRequired,
  getPlacesIsLoading } from '../redux/selectors';
import { useCurrentUser } from '../user/services';
import { useApi, deviceStorage, NONE_FILTER, makeUrl } from '../utils';
import useWorkplace from './useWorkplace';

// Places services

/** Load places in store hook. */
export function useLoadPlaces () {
  const dispatch = useDispatch();
  const api = useApi();
  const isLoading = usePlacesIsLoading();
  return [
    () => {
      dispatch(setPlacesIsLoading(true));
      deviceStorage.getItem('places').then(JSON.parse)
        .then(places => places && dispatch(loadPlaces(places)))
        .then(() => dispatch(setPlacesLoadRequired(false)))
        .then(() => api.get('/places'))
        .then(places => dispatch(loadPlaces(places)))
        .finally(() => dispatch(setPlacesIsLoading(false)))
    },
    isLoading,
  ];
};

/** Set load of places is required on 'true' in store hook. */
export function useRequirePlacesLoad() {
  const dispatch = useDispatch();
  return () => dispatch(setPlacesLoadRequired(true));
}

/** Initialize places in store hook */
export function useInitializePlaces() {
  const dispatch = useDispatch();
  return () => dispatch(initializePlaces());
}

/** Get if load of places is required from store hook. */
export function useGetPlacesLoadRequired() {
  return useSelector(state => getPlacesLoadRequired(state))
}

/** Get if load of places is pending from store hook. */
export function usePlacesIsLoading() {
  return useSelector(state => getPlacesIsLoading(state));
}

/** Add a place in store hook. */
export function useAddPlace () {
  const dispatch = useDispatch();
  return place => dispatch(addPlace(place));
};

/** Get the list of places from store hook. */
export function usePlaces () {
  return useSelector(state => getPlacesList(state));
};

/** Get the list of places by Ids from store hook. */
export function usePlacesById () {
  return useSelector(state => getPlacesById(state));
};

/** Get a place from store hook. */
export function usePlace (placeId) {
  return useSelector(state => getPlace(state, placeId));
};

/** Set a place in store hook. */
export function useSetPlace() {
  const dispatch = useDispatch();
  return place => dispatch(setPlace(place));
};

/** Delete a place from store hook. */
export function useDeletePlace() {
  const dispatch = useDispatch();
  return (id) => dispatch(deletePlace(id));
};

/** Handle a place in store if it exists or in state otherwise. */
export function usePlaceOrState(placeId, forceUpdate) {
  const api = useApi();
  const storePlace = usePlace(placeId);
  const setPlace = useSetPlace();
  const setStorePlace = place => setPlace({ref: storePlace.ref, ...place});
  const [ statePlace, setStatePlace ] = useState({id: placeId});
  const setter = storePlace ? setStorePlace : setStatePlace;
  useEffect(() => {
    if (placeId && placeId !== NONE_FILTER && (!storePlace || forceUpdate)) {
      api.get('/places/'+placeId).then(setter);
    }
  }, [placeId])
  return [(storePlace || statePlace), setter];
};

/** Handle places in store if they exist or in state otherwise. */
export const usePlacesOrState = (placeId, options={}) => {
  const api = useApi();
  const { workplace_id } = useCurrentUser();
  const isFromStore = placeId === workplace_id;
  const [statePlaces, setStatePlaces] = useState([]);
  const storePlaces = usePlaces();
  const dispatch = useDispatch();
  const setStorePlace = useSetPlace();
  const deleteStorePlace = useDeletePlace();
  useEffect(() => {
    (!isFromStore || options.include) && Promise.all([
      api.get(makeUrl('/placerefs', {workplace_id: placeId})),
      api.get(makeUrl('/places', {...options, workplace_id: placeId}))
    ])
      .then(([refs, places]) => 
        places.map(place => {
          const placeRef = refs.find(r => r.target_id === place.id)
          return ({
            ...place,
            ref: placeRef?.ref || null,
            placeRefId: placeRef?.id || null
          })
        })
      )
      .then(isFromStore
        ? places => dispatch(loadPlaces(places))
        : setStatePlaces)
  }, [placeId, isFromStore, options.include]);
  const places = isFromStore ? storePlaces : statePlaces;
  const updatePlace = isFromStore
    ? setStorePlace
    : place => setStatePlaces(places =>
        places.map(p => p.id === place.id ? place : p));
  const deletePlace = isFromStore
    ? place => deleteStorePlace(place.id)
    : place => setStatePlaces(places => 
        places.filter(({id}) => id !== place.id));
  return {places, updatePlace, deletePlace, isLoading: api.isLoading};
}

/*** Load refs and add them to places in store */
export const useAddPlaceRefs = () => {
  const api = useApi({disableConcurrentRequest: true });
  const places = usePlaces();
  const dispatch = useDispatch();
  return [
    () => {api.get('/placerefs')
      .then(refs => places.map( place => {
        place.ref = refs.find(({target_id}) => target_id === place.id)?.ref;
        return place
      }))
      .then(places => {dispatch(loadPlaces(places))})
    },
    api.isLoading
  ]
}

/*** Hook to read, create, update and delete a place ref. */
export const useManagePlaceRef = target => {
  const api = useApi();
  const workplace_id = useWorkplace().id;
  const [placeRef, setPlaceRef] = useState();
  const setPlace = useSetPlace();
  useEffect(() => {
    target?.id && api.get(`/places/${target.id}/placerefs`)
      .then(placerefs => setPlaceRef(placerefs.at(0)));
  }, [target?.id]);
  useEffect(() => {
    placeRef?.ref !== target?.ref && setPlace({...target, ref: placeRef?.ref});
  }, [placeRef?.ref])
  const handlePlaceRef = ref => {
    if (placeRef?.id && ref === '') {
      return api.delete(makeUrl(`/placerefs/${placeRef.id}`, {workplace_id}));
    } else if (placeRef?.id && ref !== placeRef.ref) {
      return api.put(
        makeUrl(`/placerefs/${placeRef.id}`, {workplace_id}),
        {ref}
      );
    } else if (!placeRef?.id && ref !== '') {
      return  api.post(
        makeUrl('/placerefs', {workplace_id}),
        {ref, target_id: target.id}
      );
    }
    return Promise.resolve(placeRef); // ref === placeRef.ref
  };
  
  return {
    placeRef,
    changeRef: ref => target?.id 
      ? handlePlaceRef(ref).then(setPlaceRef)
      : Promise.resolve()
  };
}
